import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { defineMessages } from 'react-intl';
import classNames from 'classnames';
import { Formik, useField } from 'formik';
import * as Yup from 'yup';

import { Dispatch } from 'hellospa/redux/types';
import useTriggers from 'hellospa/components/prep-and-send/trigger';
import { useAreFilesReady } from 'hellospa/components/prep-and-send/custom-hooks';
import * as actions from 'hellospa/page/prep-and-send/data/actions';
import * as types from 'hellospa/page/prep-and-send/data/types';
import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import { postTo, MessageType, useWindowMessage, EditorValidationType, canPostToParent } from 'common/utils/ts-message';
import { ConnectFormikToStepper, StepperState, stepperContext } from 'hellospa/components/prep-and-send/formik-utils';
import styles from './index.scss';
import Loading from './loading';
import { trackHeapCustomEvent, useHeapSubscription } from 'common/utils/heap';
import { usePreventNativeZoom } from 'hellospa/common/hooks';
import { useConvertDeepIntegrationFile } from 'hellospa/components/deep-integration/custom-hooks';

declare global {
  interface Window {
    closeEditor?: () => void
  }
}

const EditorNextBtnValidSchema = Yup.object().shape({
  isValid: Yup.boolean()
    .required()
    .oneOf([true]),
});

const EditorValidation = () => {
  const [, , isValidHelper] = useField<Boolean>('isValid');
  const [editorState, setEditorState] = React.useState<null | StepperState>(null);
  const dispatch = useDispatch();

  const handleEditorMessage = React.useCallback((message: EditorValidationType) => {
    isValidHelper.setValue(message.isValid);
    setEditorState({
      isBackDisabled: message.isBackDisabled,
      isValid: message.isValid,
      errorMessage: message.errorMessage,
    });
    const { numFields } = message;
    dispatch(actions.updateNumberFields(numFields));
  }, [dispatch, isValidHelper]);
  useWindowMessage(MessageType.EditorValidation, handleEditorMessage);

  const transformState = React.useCallback((state: StepperState) => {
    if (editorState) {
      return editorState;
    }
    return state;
  }, [editorState]);

  return <ConnectFormikToStepper transformState={transformState} />;
};

function useMobileAppMessages(iframeRef: React.RefObject<HTMLIFrameElement>) {
  const embeddedData = useSelector(selectors.getEmbeddedData);
  const parentUrl = embeddedData
    ? embeddedData.parentUrl
    : null;

  // When the parent window posts MobileContinue, then send EditorContinue
  useWindowMessage(MessageType.MobileContinue, () => {
    if (parentUrl == null) {
      return;
    }
    const iframe = iframeRef.current;

    if (iframe && iframe.contentWindow) {
      postTo(iframe.contentWindow, {
        type: MessageType.EditorContinue,
      });
    }
  }, { expectedOrigin: '*' });

  useHeapSubscription((eventName, properties) => {
    if (canPostToParent(parentUrl)) {
      try {
        postTo(window.parent, {
          type: MessageType.MobileHeapEvent,
          eventName,
          properties,
        }, parentUrl);
      } catch (_e) {
        // Accessing window.parent.location will throw if the parent is cross-origin.
      }
    }
  });
  useWindowMessage(MessageType.MobileHeapEvent, (message) => {
    if (canPostToParent(parentUrl)) {
      postTo(window.parent, message, parentUrl);
    }
  });

  useWindowMessage(MessageType.EditorValidation, (message) => {
    if (canPostToParent(parentUrl)) {
      postTo(window.parent, {
        ...message,
        type: MessageType.MobileContinue,
      }, parentUrl);
    }
  });
}

const PlaceFields: types.WorkflowComponent = (props, triggers) => {
  usePreventNativeZoom();
  const {
    onSaveActions,
    formRef,
    onPreviousCallbackRef,
  } = props;

  // The url isn't needed by any other component and if the Editor closes and
  // re-opens it will have to be re-fetched. Making it component state means it
  // will always start empty on mount.
  const [url, setUrl] = React.useState<string | undefined>(undefined);
  const files = useSelector(selectors.getFiles);
  const areFilesReady = useAreFilesReady(files);
  const templateOneOffFile = useSelector(selectors.hasTemplateOneOffFile);
  const isTemplate = useSelector(selectors.isTemplateRequest);

  const dispatch = useDispatch<Dispatch>();
  const [Triggers, setStage] = useTriggers(triggers, {
    load: () => { },
    submit: () => { },
    done: async () => {
      // Outside of Storybook, this will reinitilize the page with whatever data
      // the Editor just saved. Storybook doesn't have a way for the Editor to
      // save the data so that step needs to be skipped.
      //
      // I considered having the Editor `postMessage` the data back on save, but
      // shape of the data Editor sends to PHP is different from the shape P&S
      // uses. That would require me to write special conversion code that would
      // only be used in Storybook.
      if (!process.env.IS_STORYBOOK) {
        await dispatch(actions.initRefresh());
      }
      dispatch(actions.workflowOnSave(onSaveActions));
    },
  });

  React.useEffect(() => {
    let workflowType = 'one-time doc';
    if (isTemplate) {
      workflowType = 'template';
    }
    if (templateOneOffFile) {
      workflowType = 'template with one-time';
    }
    trackHeapCustomEvent('view_editor', { workflow_type: workflowType });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  useMobileAppMessages(iframeRef);

  const quickAutoSave = React.useCallback(async () => {
    const iframe = iframeRef.current;

    if (iframe && iframe.contentWindow) {
      const donePromise = new Promise<void>((resolve) => {
        const onMessage = (event: MessageEvent) => {
          if (event.origin === window.location.origin) {
            if (event.data && event.data.type === MessageType.EditorAutoSaveDone) {
              resolve();
              window.removeEventListener('message', onMessage);
            }
          }
        };
        window.addEventListener('message', onMessage);
      });

      postTo(
        iframe.contentWindow,
        { type: MessageType.EditorAutoSave },
      );
      await donePromise;

      if (!process.env.IS_STORYBOOK) {
        await dispatch(actions.initRefresh());
      }
    }
  }, [dispatch]);

  React.useEffect(() => {
    if (onPreviousCallbackRef) {
      onPreviousCallbackRef.current = quickAutoSave;
      return () => {
        onPreviousCallbackRef.current = null;
      };
    }
    return () => { };
  }, [onPreviousCallbackRef, quickAutoSave]);

  const { setStepperState } = React.useContext(stepperContext);

  React.useEffect(() => {
    if (!url && areFilesReady) {
      const run = async () => {
        await dispatch(actions.save());
        const url = await dispatch(actions.prepareEditor());
        setUrl(url);
      };
      run();
    }

    if (!url || !areFilesReady) {
      // Disabled "Next" stepper button while file converts and editor is being prepared
      setStepperState({ isValid: false });
    }
  }, [areFilesReady, dispatch, url, setStepperState]);

  useConvertDeepIntegrationFile();

  const submit = async () => {
    // tests run in JSDOM, so there isn't really a page in the iframe to postMessage to.
    if (process.env.NODE_ENV === 'test') {
      await setStage.done(); // submit it directly if it's running in a test
    } else {
      const iframe = iframeRef.current;

      if (iframe && iframe.contentWindow) {
        postTo(
          iframe.contentWindow,
          { type: MessageType.EditorContinue },
        );
      }
    }
  };
  // EditorPage looks for window.parent.$.fancybox.close on the old P&S. The new
  // P&S won't use fancybox, so it uses this `closeEditor` instead. I'm
  // intentionally replacing it on every render so it stays up to date.
  window.closeEditor = async () => {
    await setStage.done();
  };

  // Cleanup when this unmounts
  React.useEffect(() => () => {
    delete window.closeEditor;
  }, []);

  if (!areFilesReady) {
    return <Loading files={files} />;
  }

  if (!url) {
    return null;
  }

  return (
    <Formik
      initialValues={{ isValid: (process.env.NODE_ENV === 'test') }}
      onSubmit={async () => submit()}
      validateOnChange={false}
      validationSchema={EditorNextBtnValidSchema}
      innerRef={types.useFormikRef(formRef)}
      enableReinitialize={true}
      // eslint-disable-next-line react/no-children-prop
      children={() => (
        <React.Fragment>
          <Triggers />
          <EditorValidation />
          <iframe
            ref={iframeRef}
            data-qa-ref="editor-iframe"
            style={{ height: '99%', width: '100%' }}
            src={url} />
        </React.Fragment>
      )} />
  );
};

PlaceFields.getText = (workflow, state) => {
  const isStrictlyEmbedded = selectors.isStrictlyEmbedded(state);
  const isEmbeddedRequest = selectors.isEmbeddedRequest(state);
  const isForceSubjectMessage = selectors.isForceSubjectMessage(state);

  const msg = defineMessages({
    title: {
      id: '',
      description: 'title of page, also used in Stepper tab',
      defaultMessage: 'Place fields',
    },
    titleFax: {
      id: '',
      description: 'title of page, also used in Stepper tab',
      defaultMessage: 'Fill and sign',
    },
    next: {
      id: '',
      description: 'text in next button',
      defaultMessage: 'Next',
    },
    sendRequest: {
      id: '',
      description: 'text in next button for sending request on embedded',
      defaultMessage: 'Send for signature',
    },
    saveTemplate: {
      id: '',
      description: 'text in next button for saving template on embedded',
      defaultMessage: 'Save template',
    },
    save: {
      id: '',
      description: 'text in next button for saving the document for self sign',
      defaultMessage: 'Save',
    },
    skip: {
      id: '',
      description: 'Text in next button for hellofax if no components are on the editor',
      defaultMessage: 'Skip',
    },
    finish: {
      id: '',
      description: 'text in next button for completing the document for self sign',
      defaultMessage: 'Finish',
    },
  });

  if (selectors.isMeOnly(state)) {
    const integration = selectors.getIntegration(state);
    if (integration && integration.name === types.IntegrationNames.OfficeSelfSign) {
      return {
        ...msg,
        next: msg.save,
      };
    } else if (integration && integration.name === types.IntegrationNames.SharePoint) {
      return {
        ...msg,
        next: msg.finish,
      };
    }
  }

  if (!isForceSubjectMessage && isStrictlyEmbedded) {
    return {
      ...msg,
      next: isEmbeddedRequest ? msg.sendRequest : msg.saveTemplate,
    };
  }

  if (workflow.tags.includes(types.WorkflowTags.SignatureRequestFax)) {
    // For HF, if no componenent on editor, make nextButton text 'Skip'
    const numFields = selectors.getNumFields(state);
    const hfNextText = numFields === 0 ? msg.skip : msg.next;

    return {
      ...msg,
      title: msg.titleFax,
      next: hfNextText,
    };
  }

  return msg;
};

export const containerClassName = classNames(
  styles.mainContent,
  styles.placeFieldsContainer,
);

export default PlaceFields;
